// ===== WMS Layout Designer Constants =====

import type { CanvasConfig, LayerConfig } from '../types/layout-designer.types';
import { CanvasLayer } from '../types/layout-designer.types';

// --- Grid Settings ---

export const GRID_SETTINGS = {
  DEFAULT_SIZE: 20,
  MIN_SIZE: 5,
  MAX_SIZE: 100,
  SNAP_THRESHOLD: 10,
  MAJOR_LINE_EVERY: 5,
  LINE_COLOR: '#e2e8f0',
  MAJOR_LINE_COLOR: '#cbd5e0',
} as const;

// --- Zoom Settings ---

export const ZOOM_SETTINGS = {
  DEFAULT_SCALE: 1,
  MIN_SCALE: 0.1,
  MAX_SCALE: 5,
  ZOOM_STEP: 0.1,
  ZOOM_FACTOR: 1.2,
} as const;

// --- Default Canvas Size ---

export const DEFAULT_CANVAS_SIZE = {
  WIDTH: 2400,
  HEIGHT: 1600,
  PIXELS_PER_UNIT: 20,
} as const;

// --- Default Canvas Config ---

export const DEFAULT_CANVAS_CONFIG: CanvasConfig = {
  width: DEFAULT_CANVAS_SIZE.WIDTH,
  height: DEFAULT_CANVAS_SIZE.HEIGHT,
  gridSize: GRID_SETTINGS.DEFAULT_SIZE,
  snapToGrid: true,
  showGrid: true,
  showRulers: true,
  backgroundColor: '#ffffff',
  unit: 'ft',
  pixelsPerUnit: DEFAULT_CANVAS_SIZE.PIXELS_PER_UNIT,
};

// --- Zone Colors ---

export const ZONE_COLORS: Record<string, string> = {
  RECEIVING: '#c6f6d5',
  SHIPPING: '#bee3f8',
  STORAGE: '#fefcbf',
  STAGING: '#fed7e2',
  COLD: '#c4f1f9',
  HAZMAT: '#fed7d7',
  QUARANTINE: '#e9d8fd',
  RETURNS: '#feebc8',
  GENERAL: '#e2e8f0',
} as const;

// --- Element Colors ---

export const ELEMENT_COLORS = {
  RACK: {
    fill: '#4a5568',
    stroke: '#2d3748',
  },
  WALL: {
    fill: '#718096',
    stroke: '#4a5568',
  },
  DOOR: {
    fill: '#48bb78',
    stroke: '#38a169',
  },
  DOCK: {
    fill: '#4299e1',
    stroke: '#3182ce',
  },
  COLUMN: {
    fill: '#a0aec0',
    stroke: '#718096',
  },
  AISLE: {
    fill: '#edf2f7',
    stroke: '#e2e8f0',
  },
  STAGING_AREA: {
    fill: '#fefcbf',
    stroke: '#ecc94b',
  },
  LABEL: {
    fontColor: '#1a202c',
  },
} as const;

// --- Selection Colors ---

export const SELECTION_COLORS = {
  STROKE: '#3182ce',
  FILL: 'rgba(49, 130, 206, 0.1)',
  HANDLE: '#ffffff',
  HANDLE_STROKE: '#3182ce',
  MULTI_SELECT: 'rgba(49, 130, 206, 0.15)',
} as const;

// --- Heat Map Colors ---

export const HEATMAP_COLORS = {
  OCCUPANCY: {
    min: '#38a169',
    mid: '#ecc94b',
    max: '#e53e3e',
  },
  PICK_FREQUENCY: {
    min: '#ebf8ff',
    mid: '#bee3f8',
    max: '#2b6cb0',
  },
  PUTAWAY_FREQUENCY: {
    min: '#f0fff4',
    mid: '#c6f6d5',
    max: '#276749',
  },
  DEAD_STOCK: {
    min: '#fefcbf',
    mid: '#fbd38d',
    max: '#c53030',
  },
  TRAFFIC_DENSITY: {
    min: '#f0fff4',
    mid: '#fbd38d',
    max: '#c53030',
  },
} as const;

// --- Default Rack Dimensions (in grid units) ---

export const DEFAULT_RACK_DIMENSIONS = {
  WIDTH: 8,
  HEIGHT: 4,
  BAYS: 4,
  LEVELS: 4,
  BAY_WIDTH: 2,
  LEVEL_HEIGHT: 1,
} as const;

// --- Aisle Dimensions ---

export const AISLE_DIMENSIONS = {
  DEFAULT_WIDTH: 6,
  MIN_WIDTH: 3,
  MAX_WIDTH: 20,
  DEFAULT_LENGTH: 40,
} as const;

// --- Canvas Performance Limits ---

export const CANVAS_PERFORMANCE = {
  MAX_ELEMENTS: 2000,
  MAX_UNDO_STEPS: 50,
  AUTO_SAVE_INTERVAL_MS: 30000,
  DEBOUNCE_RENDER_MS: 16,
  MINIMAP_UPDATE_MS: 100,
} as const;

// --- Default Layer Configuration ---

export const DEFAULT_LAYERS: LayerConfig[] = [
  {
    id: CanvasLayer.BACKGROUND,
    name: 'Background',
    visible: true,
    locked: true,
    opacity: 1,
    order: 0,
  },
  { id: CanvasLayer.ZONES, name: 'Zones', visible: true, locked: false, opacity: 0.7, order: 1 },
  {
    id: CanvasLayer.INFRASTRUCTURE,
    name: 'Infrastructure',
    visible: true,
    locked: false,
    opacity: 1,
    order: 2,
  },
  { id: CanvasLayer.RACKS, name: 'Racks', visible: true, locked: false, opacity: 1, order: 3 },
  {
    id: CanvasLayer.EQUIPMENT,
    name: 'Equipment',
    visible: true,
    locked: false,
    opacity: 1,
    order: 4,
  },
  { id: CanvasLayer.PATHS, name: 'Paths', visible: true, locked: false, opacity: 0.6, order: 5 },
  { id: CanvasLayer.LABELS, name: 'Labels', visible: true, locked: false, opacity: 1, order: 6 },
  {
    id: CanvasLayer.OVERLAYS,
    name: 'Overlays',
    visible: true,
    locked: true,
    opacity: 0.5,
    order: 7,
  },
  {
    id: CanvasLayer.SELECTION,
    name: 'Selection',
    visible: true,
    locked: true,
    opacity: 1,
    order: 8,
  },
];

// --- Default Element Dimensions (in pixels at default zoom) ---

export const DEFAULT_ELEMENT_DIMENSIONS: Record<string, { width: number; height: number }> = {
  ZONE: { width: 200, height: 150 },
  RACK: { width: 160, height: 80 },
  AISLE: { width: 120, height: 20 },
  DOOR: { width: 40, height: 10 },
  DOCK: { width: 60, height: 40 },
  COLUMN: { width: 20, height: 20 },
  WALL: { width: 200, height: 6 },
  STAGING_AREA: { width: 150, height: 100 },
  LABEL: { width: 100, height: 30 },
} as const;

// --- Layout Export Schema Version ---

export const LAYOUT_EXPORT_SCHEMA_VERSION = '1.0.0';

// --- WebSocket Constants ---

export const LAYOUT_WEBSOCKET = {
  POSITION_BROADCAST_INTERVAL_MS: 3000,
  MAX_TRACKED_WORKERS: 100,
  STALE_POSITION_TIMEOUT_MS: 60000,
} as const;

// --- Template Categories ---

export const TEMPLATE_CATEGORIES = [
  'SMALL_WAREHOUSE',
  'DISTRIBUTION_CENTER',
  'COLD_STORAGE',
  'CUSTOM',
] as const;

export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

// --- Pick Route Visualization ---

export const PICK_ROUTE = {
  /** Average walking speed for estimated time calculation */
  AVG_WALKING_SPEED_FT_PER_MIN: 250,
  AVG_WALKING_SPEED_M_PER_MIN: 76,
  /** Max segments to render before truncation */
  MAX_RENDERED_SEGMENTS: 200,
  /** Max tasks to query from database */
  MAX_QUERY_TASKS: 100,
  /** Route segment colors by status */
  COLORS: {
    COMPLETED: '#38a169',
    IN_PROGRESS: '#3182ce',
    PENDING: '#a0aec0',
    START: '#38a169',
    END: '#e53e3e',
  },
} as const;

// --- Slotting Optimization (Phase 5D) ---

export const SLOTTING = {
  /** ABC classification thresholds (percentage of total picks) */
  A_THRESHOLD: 0.2,
  B_THRESHOLD: 0.5,
  /** Distance threshold multiplier — A-items beyond this × avg distance flagged */
  DISTANCE_THRESHOLD_MULTIPLIER: 1.5,
  /** Minimum picks to include in analysis (filter noise) */
  MIN_PICKS_THRESHOLD: 5,
  /** Analysis period in days */
  ANALYSIS_PERIOD_DAYS: 7,
  COLORS: {
    A: '#38a169',
    B: '#ecc94b',
    C: '#e53e3e',
    SUGGESTION_ARROW: '#805ad5',
  },
} as const;

// --- Traffic Density (Phase 5E) ---

export const TRAFFIC_DENSITY = {
  /** Default time ranges in hours */
  TIME_RANGES: [1, 4, 8, 24] as const,
  /** Grid cell size for aggregation (in canvas pixels) */
  GRID_CELL_SIZE: 40,
} as const;

// --- Time Slider Presets ---

export const TIME_SLIDER_PRESETS = [
  { label: '1h', value: 1, unit: 'hour' },
  { label: '4h', value: 4, unit: 'hour' },
  { label: '8h', value: 8, unit: 'hour' },
  { label: '24h', value: 24, unit: 'hour' },
  { label: '7d', value: 7, unit: 'day' },
] as const;
